import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/cms/ui/input';
import { FieldLabel, FieldHint, SectionTitle } from '@/components/ui/text';
import { ERROR_INPUT_CLASS, FIELD_ERROR_MESSAGES, type FieldKey } from '../validation';
import { FieldError } from '../FieldMarks';
import { CoverImageTile } from '../CoverImageTile';
import { LocationSearch } from '../LocationSearch';
import { PublishLocationMap } from '../PublishLocationMap';
import type { CoverEntry, PublishFlowImagesApi } from '../usePublishFlowImages';
import type { AnchorCategory } from '../types';
import { shownUrl } from '@/utils/localCopies';

const BLANK_ENTRY: CoverEntry = { id: null, url: null, originalUrl: null, crop: null };

interface Props {
  anchorCategory: AnchorCategory | undefined;
  coverImages: PublishFlowImagesApi;
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  address: string;
  latitude: string;
  longitude: string;
  errors: Set<FieldKey>;
  onChange: (patch: { address?: string; latitude?: string; longitude?: string; city?: string }) => void;
}

export function AnchorInfoStep(props: Props) {
  if (props.anchorCategory === 'place_anywhere' || !props.anchorCategory) {
    // Routing should skip this step entirely for tap-to-place scenes.
    return null;
  }

  return <AnchorInfoStepBody {...props} />;
}

function AnchorInfoStepBody({
  anchorCategory,
  coverImages,
  projectInstanceId,
  sceneOriginId,
  address,
  latitude,
  longitude,
  errors,
  onChange,
}: Props) {
  const isImageAnchor = anchorCategory === 'image_anchor';
  const isImmersal = anchorCategory === 'immersal';
  const leftTitle = isImageAnchor ? 'Image anchor' : 'Location image';
  const locationRequired = !isImageAnchor;
  const imageError = errors.has('anchorImage');
  const addressError = errors.has('anchorAddress');
  const isAutoLocationImage = isImmersal && coverImages.scanImageIsAutoThumbnail;
  const locationImageUrl = isAutoLocationImage ? null : coverImages.scanImageUrl;

  const mapRef = useRef<HTMLDivElement>(null);
  const [scanHeightPx, setScanHeightPx] = useState<number>(0);

  useEffect(() => {
    const compute = () => {
      if (!mapRef.current) return;
      const h = mapRef.current.getBoundingClientRect().height;
      if (h > 0) setScanHeightPx(h);
    };
    compute();
    const obs = new ResizeObserver(compute);
    if (mapRef.current) obs.observe(mapRef.current);
    window.addEventListener('resize', compute);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  return (
    <div className="grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line h-full">
      <div className="flex flex-col min-w-0 pr-7">
        <div className="h-12">
          <SectionTitle>{leftTitle}</SectionTitle>
          {!isImageAnchor && (
            <FieldHint>Let your audience know where to point their camera.</FieldHint>
          )}
        </div>

        {isImageAnchor ? (
          <>
            <div
              className={`mt-3 w-full rounded-lureo-radius border-lureo-border-width bg-lureo-neutral-subtle overflow-hidden flex items-center justify-center ${
                imageError ? 'border-lureo-danger-ink' : 'border-lureo-line'
              }`}
              style={scanHeightPx > 0 ? { height: `${scanHeightPx}px` } : { aspectRatio: '16/9' }}
            >
              {coverImages.imageAnchorUrl ? (
                <img src={shownUrl(coverImages.imageAnchorUrl)} alt="" className="w-full h-full object-contain" />
              ) : (
                <span className="font-lureo-font-ui text-lureo-13 text-lureo-ink-subtlest leading-[1.3]">
                  Auto-populated later
                </span>
              )}
            </div>
            {imageError ? (
              <FieldError>{FIELD_ERROR_MESSAGES.anchorImage}</FieldError>
            ) : (
              <FieldHint>Your audience points their camera at this image to start your story.</FieldHint>
            )}
          </>
        ) : (
          <>
            <div className="mt-3">
              <CoverImageTile
                aspect="1/1"
                heightPx={scanHeightPx > 0 ? scanHeightPx : undefined}
                label="Location image (1:1)"
                category="ScanImage"
                entry={isAutoLocationImage ? BLANK_ENTRY : coverImages.scanImage}
                displayUrl={locationImageUrl}
                error={imageError}
                projectInstanceId={projectInstanceId}
                sceneOriginId={sceneOriginId}
                onSave={coverImages.saveScanImage}
                onRemove={coverImages.removeScanImage}
              />
            </div>
            {imageError ? (
              <FieldError>Add a location image</FieldError>
            ) : (
              <FieldHint>
                {isImmersal
                  ? 'Tip: upload a clear, recognisable photo of the location.'
                  : 'Pre-filled from the map — keep it or upload your own.'}
              </FieldHint>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col min-w-0 pl-7 h-full">
        <div className="h-12 flex items-start justify-between gap-3">
          <SectionTitle>Location{locationRequired ? '' : ' (optional)'}</SectionTitle>
          <LocationSearch className="w-56" onChange={onChange} />
        </div>
        <div ref={mapRef} className="flex-1 min-h-0 mt-3 mb-3">
          <PublishLocationMap
            latitude={latitude}
            longitude={longitude}
            address={address}
            onChange={onChange}
          />
        </div>
        <div className="mb-2">
          <div className="flex items-center gap-3">
            <FieldLabel className="w-24 shrink-0">Address</FieldLabel>
            <Input
              className={`flex-1 ${addressError ? ERROR_INPUT_CLASS : ''}`}
              value={address}
              onChange={(e) => onChange({ address: e.target.value })}
              placeholder="Address"
            />
          </div>
          {addressError && <FieldError>{FIELD_ERROR_MESSAGES.anchorAddress}</FieldError>}
        </div>
        <div className="flex items-center gap-3">
          <FieldLabel className="w-24 shrink-0">Coords</FieldLabel>
          <Input
            className="flex-1"
            value={latitude}
            onChange={(e) => onChange({ latitude: e.target.value })}
            placeholder="Lat"
          />
          <Input
            className="flex-1"
            value={longitude}
            onChange={(e) => onChange({ longitude: e.target.value })}
            placeholder="Lng"
          />
        </div>
      </div>
    </div>
  );
}
